import { useEffect, useRef } from 'react';

/** A key press is someone typing, not a shortcut. */
export const typing = (target: EventTarget | null) =>
  target instanceof HTMLElement && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

/**
 * A menu or dialog is open: its keys (Esc to close it, above all) are its own. `own` is a dialog whose keys
 * the caller handles itself, so it alone doesn't count.
 */
export const layerOpen = (own?: Element | null) =>
  [...document.querySelectorAll('[role="menu"], [role="dialog"], [role="alertdialog"]')].some((layer) => layer !== own);

/**
 * A ledger's selection keys: X toggles the row in focus (the `tr[data-prospect-id]` holding it), S opens
 * the add, Esc clears. Never while typing, and never while `paused` (a dialog open with keys of its own).
 */
export function useSelectionKeys({
  onToggle,
  onAdd,
  onClear,
  hasSelection,
  paused,
}: {
  onToggle: (prospectId: string) => void;
  onAdd?: () => void;
  onClear: () => void;
  hasSelection: boolean;
  paused: boolean;
}) {
  // Read at key time, so the listener is added once rather than on every render.
  const latest = useRef({ onToggle, onAdd, onClear, hasSelection, paused });
  latest.current = { onToggle, onAdd, onClear, hasSelection, paused };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const now = latest.current;
      if (e.metaKey || e.ctrlKey || e.altKey || now.paused || typing(e.target) || layerOpen()) return;
      const key = e.key.toLowerCase();
      if (key === 'x') {
        const id = (document.activeElement as HTMLElement | null)?.closest<HTMLElement>('tr[data-prospect-id]')?.dataset.prospectId;
        if (!id) return;
        e.preventDefault();
        now.onToggle(id);
      } else if (key === 's' && now.hasSelection && now.onAdd) {
        e.preventDefault();
        now.onAdd();
      } else if (e.key === 'Escape' && now.hasSelection) {
        now.onClear();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
